import { type FormEvent, useState } from "react";
import { t } from "i18next";
import { toast } from "sonner";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { AuthGuard, useAuth } from "@/lib/auth";

export const Route = createFileRoute("/change-password")({
	component: ChangePasswordRoute,
});

function ChangePasswordRoute() {
	return (
		<AuthGuard>
			<ChangePasswordPage />
		</AuthGuard>
	);
}

function ChangePasswordPage() {
	const { changePassword } = useAuth();
	const navigate = useNavigate();

	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (newPassword.length < 12) {
			toast.error(t("Password must be at least 12 characters"));
			return;
		}
		if (newPassword !== confirmPassword) {
			toast.error(t("Passwords do not match"));
			return;
		}
		setIsSubmitting(true);
		try {
			await changePassword({ currentPassword, newPassword });
			toast.success(t("Password updated"));
			await navigate({ to: "/bookings" });
		} catch (error) {
			const message =
				error instanceof Error ? error.message : t("Could not update password");
			toast.error(message);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				bgcolor: "background.level1",
				p: 2,
			}}
		>
			<Paper
				component="form"
				onSubmit={handleSubmit}
				sx={{
					width: { xs: "100%", sm: 420 },
					p: { xs: 3, sm: 4 },
					borderRadius: 2,
				}}
			>
				<Stack spacing={3}>
					<Stack spacing={0.5}>
						<Typography variant="h5">{t("Change your password")}</Typography>
						<Typography variant="body2" color="text.secondary">
							{t("Set a new password before continuing.")}
						</Typography>
					</Stack>
					<TextField
						label={t("Current password")}
						type="password"
						autoComplete="current-password"
						value={currentPassword}
						onChange={(event) => setCurrentPassword(event.target.value)}
						required
						fullWidth
					/>
					<TextField
						label={t("New password")}
						type="password"
						autoComplete="new-password"
						value={newPassword}
						onChange={(event) => setNewPassword(event.target.value)}
						inputProps={{ minLength: 12 }}
						required
						fullWidth
					/>
					<TextField
						label={t("Confirm new password")}
						type="password"
						autoComplete="new-password"
						value={confirmPassword}
						onChange={(event) => setConfirmPassword(event.target.value)}
						required
						fullWidth
					/>
					<Button
						type="submit"
						variant="contained"
						size="large"
						disabled={isSubmitting}
					>
						{isSubmitting ? t("Saving…") : t("Save new password")}
					</Button>
				</Stack>
			</Paper>
		</Box>
	);
}
